import { memo, useRef, useMemo } from "react";
import { CSVLink } from "react-csv";
import createPersistedState from "use-persisted-state";
import { Button, Tooltip } from "@edgetier/components";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import { IProps } from "./download-comparison-table-button.types";
import { generateAgentsCsvFileContents } from "./download-comparison-table-button.utilities";
import useTags from "hooks-for/tags/use-tags";
import {
    EXPERIENCE_SCORE_METRIC_ENABLED,
    GRAMMAR_METRIC_ENABLED,
    SPELLING_METRIC_ENABLED,
} from "constants/feature-flags";
import { PersistedStorageKeys } from "constants/persisted-storage-keys";
import "./download-comparison-table-button.scss";

const useTagSelected = createPersistedState<string[]>(PersistedStorageKeys.TagSelected);

/**
 * A button to download all agents to a CSV file
 * @param props.comparisonData        Data for all groups.
 * @param props.overallComparisonData Overall details.
 * @returns                           Button to download data.
 */
const DownloadComparisonTableButton = <Group extends {}>({
    comparisonData,
    groupPluralTitle,
    headingProperties,
    overallComparisonData,
}: IProps<Group>) => {
    const csvLink = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);
    const { data: tags } = useTags();
    const [tagSelected] = useTagSelected([]);

    const csvFilename = `watchtower-${groupPluralTitle.toLocaleLowerCase()}.csv`;
    const customTags = tags?.filter((tag) => tagSelected.includes(tag.tagId));

    /**
     * Create the CSV file contents
     */
    const downloadAllAgents = useMemo(() => {
        if (typeof comparisonData !== "undefined" && typeof overallComparisonData !== "undefined") {
            const csvKeys = Object.keys(headingProperties).concat([
                "Interaction Count",
                "Average Handling Time (Seconds)",
                "Average Response Time (Seconds)",
                "NPS",
                "CSAT",
                "Resolution Rate",
                ...(EXPERIENCE_SCORE_METRIC_ENABLED ? ["Experience Score"] : []),
                "Frustration",
                "Praise",
                ...(SPELLING_METRIC_ENABLED ? ["Spelling"] : []),
                ...(GRAMMAR_METRIC_ENABLED ? ["Grammar"] : []),
                ...(typeof customTags !== "undefined" ? customTags.map(({ tag }) => tag) : []),
            ]);

            return generateAgentsCsvFileContents(csvKeys, headingProperties, comparisonData, overallComparisonData, {
                hasPercentageTag: typeof customTags !== "undefined",
            });
        }
    }, [comparisonData, headingProperties, overallComparisonData, customTags]);

    return (
        <div>
            <Tooltip content={`Download ${groupPluralTitle}`} delayEnter={500}>
                <Button
                    icon={faDownload}
                    isIconOnly
                    outline
                    size="small"
                    onClick={() => csvLink.current?.link.click()}
                />
            </Tooltip>

            <CSVLink data={downloadAllAgents ?? ""} filename={csvFilename} target="_blank" ref={csvLink} />
        </div>
    );
};

export default memo(DownloadComparisonTableButton) as typeof DownloadComparisonTableButton;
